import { useMemo } from 'react';

import { Action, ActionBuilderContext, ADMISSION_CARD_TYPE, AdmissionCardActionsConfig } from './type';

const pushGroupCardActions = (context: ActionBuilderContext) => {
  const { actions, config, t } = context;
  const { isPreset, isGroupCard, handleAddGroup, handleRemoveFromGroup, handleMoveToAnotherGroup } = config;

  if (!isGroupCard && !isPreset) {
    actions.push({ name: t('admissionGroups:addAdmissionToGroupActionLabel'), action: handleAddGroup });
  }

  if (isGroupCard && !isPreset) {
    actions.push({ name: t('admissionGroups:moveToGroupActionLabel'), action: handleMoveToAnotherGroup });
  }

  if (isGroupCard) {
    actions.push({ name: t('admissionGroups:removeAdmissionToGroupActionLabel'), action: handleRemoveFromGroup });
  }
};

/**
 * Action builder for Pass cards
 */
const buildPassActions = (context: ActionBuilderContext): Action[] => {
  const { actions, config, t } = context;
  const {
    isPreset,
    isToggleActive,
    isExportQrCodesActive,
    onDelete,
    onDeletePass,
    onEdit,
    isSharePaymentLinkActive,
    deleteIsUnassign,
  } = config;
  const {
    handleAddHolder,
    handleDelete,
    handleDuplicate,
    handleDeletePass,
    handleEdit,
    handleViewHolders,
    handleViewEvents,
    handleExportQrCodes,
    handleShareLink,
  } = config;

  // Add holder (only for presets)
  if (isPreset) {
    actions.push({
      name: t('passes:addHolderButton'),
      action: handleAddHolder,
    });
  }

  // Duplicate (only for presets)
  if (isPreset && config.onDuplicate) {
    actions.push({
      name: t('common:duplicate'),
      action: handleDuplicate,
    });
  }

  // Edit (only if toggle is active)
  if (onEdit && isToggleActive) {
    actions.push({
      name: t('common:edit'),
      action: handleEdit,
      dataTestId: 'admission-card-menu-item--edit',
    });
  }

  // View holders (always available)
  actions.push({
    name: t('common:viewHolders'),
    action: handleViewHolders,
  });

  // View events (only for presets)
  if (isPreset && config.onClickViewEvents) {
    actions.push({
      name: t('passes:viewEvents'),
      action: handleViewEvents,
    });
  }

  // Export QR codes (only for presets, gated by EXPORT_PASS_HOLDERS permission)
  if (isPreset && isExportQrCodesActive && config.onClickExportQrCodes) {
    actions.push({
      name: t('passes:exportQrCodes'),
      action: handleExportQrCodes,
    });
  }

  // Share payment link
  if (isSharePaymentLinkActive) {
    actions.push({
      name: t('common:sharePaymentLink'),
      action: handleShareLink,
    });
  }

  pushGroupCardActions(context);

  // Delete/Unassign (only if toggle is active)
  if (onDelete && isToggleActive) {
    actions.push({
      name: t(`common:${deleteIsUnassign ? 'unassign' : 'delete'}`),
      action: handleDelete,
    });
  }

  // Delete company pass
  if (onDeletePass) {
    actions.push({
      name: t('common:delete'),
      action: handleDeletePass,
      dataTestId: 'admission-card-menu-item--delete',
    });
  }

  return actions;
};

/**
 * Action builder for Regular admission cards
 */
const buildRegularAdmissionActions = (context: ActionBuilderContext): Action[] => {
  const { actions, config, t } = context;
  const { isPreset, isPastEvent, isOpenForAll, deleteIsUnassign, isGroupCard, isEnabledSection } = config;
  const {
    handleEdit,
    handleDuplicate,
    handleDelete,
    handleShareLink,
    handleUnassign,
    handleAddGroup,
    handleRemoveFromGroup,
    handleMoveToAnotherGroup,
  } = config;

  // Edit (always first)
  actions.push({
    name: t('common:edit'),
    action: handleEdit,
    dataTestId: 'admission-card-menu-item--edit',
  });

  // Duplicate (not for presets)
  if (!isPreset) {
    actions.push({
      name: t('common:duplicate'),
      action: handleDuplicate,
    });
  }

  if (!isGroupCard && isEnabledSection) {
    actions.push({
      name: t('admissionGroups:addAdmissionToGroupActionLabel'),
      action: handleAddGroup,
    });
  }

  if (isGroupCard) {
    actions.push(
      { name: t('admissionGroups:moveToGroupActionLabel'), action: handleMoveToAnotherGroup },
      { name: t('admissionGroups:removeAdmissionToGroupActionLabel'), action: handleRemoveFromGroup }
    );
  }

  // Share payment link (only if open for all)
  if (isOpenForAll) {
    actions.push({
      name: t('common:sharePaymentLink'),
      action: handleShareLink,
    });
  }

  // Delete (not for past events, only if handler is provided)
  if (!isPastEvent && config.onDelete) {
    actions.push({
      name: t('common:delete'),
      action: handleDelete,
      dataTestId: 'admission-card-menu-item--delete',
    });
  }

  // Unassign (if applicable)
  if (deleteIsUnassign) {
    actions.push({
      name: t('common:unassign'),
      action: handleUnassign,
    });
  }

  return actions;
};

/**
 * Extensible action builders registry
 * Add new admission types here in the future
 */
const ACTION_BUILDERS: Record<AdmissionCardActionsConfig['type'], (context: ActionBuilderContext) => Action[]> = {
  [ADMISSION_CARD_TYPE.pass]: buildPassActions,
  [ADMISSION_CARD_TYPE.offer]: buildRegularAdmissionActions,
};

/**
 * Custom hook to build admission card actions based on type and conditions
 *
 * @param config - Configuration object with card type, conditions, and handlers
 * @returns Array of actions to display in the submenu
 */
export const useAdmissionCardActions = (config: AdmissionCardActionsConfig): Action[] => {
  return useMemo(() => {
    const { type, t } = config;
    const builder = ACTION_BUILDERS[type];

    if (!builder) {
      console.warn(`No action builder found for type: ${type}`);
      return [];
    }

    const context: ActionBuilderContext = {
      actions: [],
      config,
      t,
    };

    return builder(context);
  }, [config]);
};
